import {
  Editor,
  DowncastAttributeEvent,
  DowncastDispatcher,
  ViewElement,
  ListEditing,
  Paragraph,
  ButtonView,
  Locale,
} from "ckeditor5";

import { PluginWithStyles } from "../PluginWithStyles";

import { MarkBlocksCommand } from "./command";
import {
  createDefaultAttributeToAttributeDowncast,
  createDowncastStrategy,
  downcastMarkedListItem,
} from "./downcast";
import { buildEditorStyles } from "./styles";
import {
  ATTRIBUTE_NAME,
  CLASS_REGEX,
  MarkBlocksConfig,
  MarkBlocksConfigItem,
} from "./utils";

export default class MarkBlocksPlugin extends PluginWithStyles {
  config: MarkBlocksConfig;

  constructor(editor: Editor) {
    super(editor);
    this.config = editor.config.get("markBlocks") ?? {};
  }

  public static get requires() {
    return [Paragraph, ListEditing];
  }

  public static buildEditorStyles = buildEditorStyles;

  public init() {
    for (const schemaItemName of ["$container", "$block", "$blockObject"]) {
      this.editor.model.schema.extend(schemaItemName, {
        allowAttributes: [ATTRIBUTE_NAME],
      });
    }

    for (const markName of Object.keys(this.config)) {
      this.editor.ui.componentFactory.add(markName, (locale) =>
        this.createButtonAndCommand(locale, markName, this.config[markName]),
      );
    }

    this.setupConversions();
  }

  setupConversions() {
    //
    // List editing downcast strategy - prevents our default downcast from firing
    // for list items, and prevents the list plugin from wrapping item content in
    // unnecessary <p> tags
    //
    this.editor.plugins
      .get(ListEditing)
      .registerDowncastStrategy(createDowncastStrategy());

    //
    // Note: in a list, this upcast will apply the mark attribute to all of the
    // paragraphs nested under the <li> that has the class, not just the paragraphs
    // that are directly part of that <li>. This is inconsistent with the MarkBlocksCommand;
    // it applies the attribute only to the selected list item, and assumes inheritance to
    // its children.
    //
    // This will cause incorrect behavior if we ever try to have two different "kinds"
    // of marker interacting in the same content.
    //
    this.editor.conversion.for("upcast").attributeToAttribute({
      view: {
        key: "class",
        value: CLASS_REGEX,
      },
      model: {
        key: ATTRIBUTE_NAME,
        value: (e: ViewElement) => {
          const match = e.getAttribute("class")?.match(CLASS_REGEX);
          return match?.[1];
        },
      },
    });

    this.editor.conversion
      .for("dataDowncast")
      .attributeToAttribute(createDefaultAttributeToAttributeDowncast())
      .add(downcastMarkedListItem);

    this.editor.conversion
      .for("editingDowncast")
      .attributeToAttribute(createDefaultAttributeToAttributeDowncast())
      .add(downcastMarkedListItem)
      .add(this.updateItemContentEditable());
  }

  updateItemContentEditable() {
    //
    // It would be better if we could make the item into a widget in the editing view;
    // we could then have controls and a more reliable user experience.
    // Unfortunately I couldn't immediately get that working at this low level, and didn't
    // want to burn a bunch of time figuring out how to do it.
    //
    // Instead we use the contenteditable attribute directly along with some CSS
    // to make a widget-like presentation.
    //
    return (dispatcher: DowncastDispatcher) => {
      dispatcher.on<DowncastAttributeEvent>(
        `attribute:${ATTRIBUTE_NAME}`,
        (e, { item }, { writer, mapper }) => {
          if (!item.is("model:element")) return;
          const viewElement = mapper.toViewElement(item);
          if (viewElement == null) return;

          const markName = item.getAttribute(ATTRIBUTE_NAME) as string;

          writer.setAttribute(
            "contenteditable",
            !this.config?.[markName]?.readonly,
            viewElement,
          );
        },
      );
    };
  }

  createButtonAndCommand(
    locale: Locale,
    markName: string,
    config: MarkBlocksConfigItem,
  ) {
    const command = new MarkBlocksCommand(this.editor, markName);
    this.editor.commands.add(`mark${markName}`, command);

    const button = new ButtonView(locale);

    button.set({
      label: this.editor.t(config.label),
      icon: config.icon,
      withText: config.showLabel,
      tooltip: config.label,
      isToggleable: true,
    });

    button.on("execute", () => {
      this.editor.execute(`mark${markName}`);
      this.editor.editing.view.focus();
    });

    button.bind("isOn", "isEnabled").to(command, "value", "isEnabled");

    return button;
  }
}
